/**
 * Generic hook for per-arche user settings stored in localStorage.
 */

import { useState, useCallback } from 'react';

const SETTINGS_KEY_PREFIX = 'fias:arche-settings:';

function getStorageKey(archeId: string): string {
  return `${SETTINGS_KEY_PREFIX}${archeId}`;
}

function loadSettings<T>(archeId: string, defaults: T): T {
  try {
    const raw = localStorage.getItem(getStorageKey(archeId));
    if (!raw) return defaults;
    return { ...defaults, ...JSON.parse(raw) };
  } catch {
    return defaults;
  }
}

function saveSettings<T>(archeId: string, settings: T): void {
  try {
    localStorage.setItem(getStorageKey(archeId), JSON.stringify(settings));
  } catch {
    // localStorage full or unavailable — silently ignore
  }
}

export function useArcheSettings<T extends object>(
  archeId: string,
  defaults: T,
): [T, (update: Partial<T>) => void] {
  const [settings, setSettings] = useState<T>(() => loadSettings(archeId, defaults));

  const updateSettings = useCallback(
    (update: Partial<T>) => {
      setSettings((prev) => {
        const next = { ...prev, ...update };
        saveSettings(archeId, next);
        return next;
      });
    },
    [archeId],
  );

  return [settings, updateSettings];
}
